import React, { ChangeEvent, CSSProperties } from 'react';
import { Form } from 'react-bootstrap';
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form';
import { InputContainer } from '../styles';

type CaseInput = 'lowercase' | 'uppercase' | 'none';

const CEST_FIELD_NAME = 'des_cest';

const CEST_TEXT_AREA_STYLE: CSSProperties = {
  resize: 'none',
  height: '12.5rem',
};

interface InputTextAreaProps extends React.HTMLProps<HTMLTextAreaElement> {
  label?: string;
  placeholder?: string;
  name: string;
  register: UseFormRegister<FieldValues>;
  isError?: boolean;
  maxLength: number;
  control: Control<FieldValues, any>;
  resize?: boolean;
  caseInput?: CaseInput;
}

const getTextAreaClassName = (isError?: boolean, className?: string) =>
  ['form-control', isError ? 'is-invalid' : '', className || '']
    .filter(Boolean)
    .join(' ');

const getDefaultStyle = (
  name: string,
  resize: boolean,
  caseInput: CaseInput,
): CSSProperties => {
  if (name === CEST_FIELD_NAME) return CEST_TEXT_AREA_STYLE;

  return {
    resize: resize ? 'vertical' : 'none',
    textTransform: caseInput,
  };
};

const getTextAreaValue = (value: unknown, name: string) => {
  const textValue = String(value ?? '');

  if (name === CEST_FIELD_NAME) return textValue.toUpperCase().trimStart();

  return textValue;
};

const applyCaseInput = (value: string, caseInput: CaseInput) => {
  if (caseInput === 'uppercase') return value.toUpperCase();
  if (caseInput === 'lowercase') return value.toLowerCase();
  return value;
};

const getNextValue = (value: string, name: string, caseInput: CaseInput) => {
  if (name === CEST_FIELD_NAME) return value.toUpperCase().trimStart();
  return applyCaseInput(value, caseInput);
};

export const InputTextArea: React.FC<InputTextAreaProps> = ({
  label = '',
  placeholder = '',
  name,
  register,
  isError,
  maxLength,
  control,
  resize = true,
  caseInput = 'none',
  className,
  style,
  onChange,
  ...rest
}) => {
  const registeredField = register(name);
  const textAreaClassName = getTextAreaClassName(isError, className);
  const textAreaStyle = {
    ...getDefaultStyle(name, resize, caseInput),
    ...style,
  };

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>

        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange: fieldOnChange } }) => {
            return (
              <textarea
                {...registeredField}
                {...rest}
                maxLength={maxLength}
                className={textAreaClassName}
                value={getTextAreaValue(value, name)}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  const nextValue = getNextValue(
                    event.target.value,
                    name,
                    caseInput,
                  );

                  fieldOnChange(nextValue);
                  onChange?.(event);
                }}
                placeholder={placeholder}
                style={textAreaStyle}
              />
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
